import { useEffect, useRef, useState } from 'react';
import { studioApi, idempotencyKey, errorKey } from '../../../data/studioApi';

const POLL_MS = 2000;
// The webhook usually lands in seconds; past this (Pix, a slow bank) the dialog says
// the credits arrive once the payment is confirmed, and stops waiting.
const POLL_LIMIT_MS = 60000;
// The upgrade preview is read again well inside the 30 minutes the core accepts a
// pinned `proration_date`, so the amount on the button is always what gets charged.
const PREVIEW_REFRESH_MS = 10 * 60000;
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

// One purchase, step by step:
//   offer       the plan (or packs) and the CTA;
//   checkout    Stripe embedded checkout for a new subscription or a pack purchase;
//   confirming  paid in Stripe, waiting for the webhook to grant (or the upgrade call);
//   action      an upgrade held by 3DS or a declined card (`pending_change`);
//   processing  paid, not confirmed yet (Pix or a slow webhook);
//   done        granted.
// A subscriber upgrades without a checkout: `PATCH /subscription` charges the card on file
// what the preview showed (`GET /subscription/preview`, pinned by its `proration_date`).
// Without a plan the creator may pick any paid plan (`start`, or `choosePlan`) before paying.
export default function usePlanCheckout({ studio: s, kind, plan: initialPlan, quantity: initialQuantity }) {
  const p = s.payments;
  const presets = p.pack?.presets || [];
  const [plan, setPlan] = useState(initialPlan);
  const [quantity, setQuantity] = useState(initialQuantity || presets[1] || presets[0] || p.pack?.min_quantity || 10);
  const [step, setStep] = useState(kind === 'credits' && initialQuantity ? 'checkout' : 'offer');
  const [checkout, setCheckout] = useState(null);
  const [pending, setPending] = useState(null);
  const [granted, setGranted] = useState(null);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const key = useRef(idempotencyKey('checkout'));
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const upgrading = kind === 'subscription' && !!p.subscription?.id;
  const renewKey = () => { key.current = idempotencyKey('checkout'); };
  const failWith = failure => { if (!alive.current) return; setError(errorKey(failure)); setStep('offer'); setCheckout(null); renewKey(); };

  // While the upgrade offer is open: the amount the card on file pays now.
  useEffect(() => {
    if (!upgrading || step !== 'offer') return undefined;
    let live = true;
    const load = () => studioApi.subscriptionPreview(plan)
      .then(next => { if (live && alive.current) setPreview(next); })
      .catch(failure => { if (live && alive.current) { setPreview(null); setError(errorKey(failure)); } });
    load();
    const timer = setInterval(load, PREVIEW_REFRESH_MS);
    return () => { live = false; clearInterval(timer); };
  }, [upgrading, plan, step]);

  // A session is created when the checkout step opens; a retry reuses its key.
  useEffect(() => {
    if (step !== 'checkout' || checkout) return;
    studioApi.createCheckout(kind === 'credits' ? { kind, quantity } : { kind, plan }, key.current)
      .then(created => alive.current && setCheckout(created))
      .catch(failWith);
  }, [step, checkout]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = async (units, planChanged) => {
    await p.refresh({ planChanged });
    if (!alive.current) return;
    if (s.error === 'credits') s.setError(null);
    setGranted(units);
    setStep('done');
  };

  // Stripe confirmed the payment on its side; the credits come with the webhook.
  const complete = async () => {
    const id = checkout.id;
    setStep('confirming');
    const started = Date.now();
    try {
      while (alive.current) {
        const current = await studioApi.checkout(id);
        if (current.fulfillment === 'fulfilled') { await finish(current.units_granted, current.kind === 'subscription'); return; }
        if (current.fulfillment === 'failed' || ['expired', 'failed'].includes(current.status)) {
          failWith({ studio: true, code: 'payment_failed' });
          return;
        }
        if (Date.now() - started >= POLL_LIMIT_MS) { setStep('processing'); return; }
        await wait(POLL_MS);
      }
    } catch (failure) {
      // The payment went through; only the confirmation read failed.
      if (alive.current) setStep('processing');
    }
  };

  // The upgrade is charged now on the card on file; 3DS or a decline hold it as
  // `pending_change` and the creator stays on the current plan until it is paid.
  const upgrade = async () => {
    setError(null);
    setStep('confirming');
    try {
      const pinned = preview?.plan === plan ? { proration_date: preview.proration_date } : {};
      const next = await studioApi.updateSubscription({ plan, ...pinned }, key.current);
      if (!alive.current) return;
      p.setSubscription(next);
      if (next.pending_change) { setPending(next.pending_change); setStep('action'); renewKey(); return; }
      await finish(next.allowance?.monthly_units ?? null, true);
    } catch (failure) { failWith(failure); }
  };

  const begin = () => {
    setError(null);
    if (upgrading) return upgrade();
    setStep('checkout');
    return null;
  };
  // Back to the offer (another pack, or reading the plan again). The open session is
  // dropped; the core reuses it if the same plan and currency come back.
  const back = () => { setCheckout(null); renewKey(); setStep('offer'); };
  const choose = next => { setQuantity(next); renewKey(); };
  const choosePlan = next => { setPlan(next); setError(null); renewKey(); };
  // Without a plan each card pays for itself: its plan goes straight to the checkout.
  const start = next => { if (next !== plan) choosePlan(next); setError(null); setStep('checkout'); };

  // After paying the held invoice (or changing the card) the subscription is read again.
  const recheck = async () => {
    try {
      const current = await studioApi.subscription();
      if (!alive.current) return;
      p.setSubscription(current);
      if (current.plan === plan && !current.pending_change) { await finish(current.allowance?.monthly_units ?? null, true); return; }
      setPending(current.pending_change);
      setError(current.pending_change ? 'paymentPending' : 'subscriptionChanged');
    } catch (failure) { if (alive.current) setError(errorKey(failure)); }
  };

  const openPortal = async () => {
    try {
      const session = await studioApi.createPortalSession(idempotencyKey('portal'));
      window.open(session.url, '_blank', 'noopener,noreferrer');
    } catch (failure) { if (alive.current) setError(errorKey(failure)); }
  };

  return { step, plan, choosePlan, quantity, choose, checkout, pending, granted, error, preview, upgrading, begin, start, back, complete, recheck, openPortal };
}
